import { useEffect, useRef, useState } from "react";
import { MapPin, MagnifyingGlass, Spinner } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import api from "../lib/api";

/**
 * Autocompletado de direcciones estilo Google Maps, pero usando Nominatim (OpenStreetMap).
 * Al seleccionar una sugerencia, dispara onSelect con:
 *   { display_name, direccion_calle, direccion_numero, direccion_localidad,
 *     direccion_provincia, direccion_cp, lat, lon }
 *
 * Props:
 *   value: string        — texto actual del input (controlado por el padre)
 *   onChange(v: string)  — texto libre (mientras el usuario tipea)
 *   onSelect(place)      — cuando el usuario elige una sugerencia
 *   placeholder, autoFocus, className, testId (opcionales)
 */
export function AddressAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Buscá tu dirección (calle, número, localidad)",
  autoFocus = false,
  className = "",
  testId = "address-autocomplete",
}) {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Mantener sincronizado con la prop value (edición externa)
  useEffect(() => { setQ(value || ""); }, [value]);

  // Cerrar al click fuera
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = async (text) => {
    if (!text || text.trim().length < 3) {
      setItems([]); setOpen(false); return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/geocode/autocomplete", { params: { q: text, limit: 6 } });
      setItems(Array.isArray(data) ? data : []);
      setOpen(true);
      setHighlight(-1);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (v) => {
    setQ(v);
    onChange && onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 350);
  };

  const handlePick = (place) => {
    setOpen(false);
    const label = [place.direccion_calle, place.direccion_numero].filter(Boolean).join(" ");
    setQ(label || place.display_name || "");
    onChange && onChange(label || place.display_name || "");
    onSelect && onSelect(place);
  };

  const handleKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") {
      if (highlight >= 0 && items[highlight]) { e.preventDefault(); handlePick(items[highlight]); }
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      <div className="relative">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => q && items.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="pl-8 pr-8"
          data-testid={`${testId}-input`}
        />
        {loading && <Spinner size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-500 animate-spin" />}
      </div>

      {open && items.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-neutral-300 rounded-md shadow-lg max-h-72 overflow-auto"
          data-testid={`${testId}-dropdown`}
        >
          {items.map((it, idx) => (
            <button
              key={idx}
              type="button"
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => handlePick(it)}
              className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-neutral-100 last:border-b-0 transition-colors ${idx === highlight ? "bg-[#921A0E]/10" : "hover:bg-neutral-50"}`}
              data-testid={`${testId}-item-${idx}`}
            >
              <MapPin size={16} weight="fill" className="text-[#921A0E] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {[it.direccion_calle, it.direccion_numero].filter(Boolean).join(" ") || it.display_name}
                </div>
                <div className="text-[11px] text-neutral-500 truncate">
                  {[it.direccion_localidad, it.direccion_provincia, it.direccion_cp && `CP ${it.direccion_cp}`]
                    .filter(Boolean).join(" · ")}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && !loading && items.length === 0 && q.trim().length >= 3 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-neutral-300 rounded-md shadow-lg px-3 py-2 text-xs text-neutral-500" data-testid={`${testId}-empty`}>
          Sin resultados para "{q}". Podés cargar los campos manualmente.
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
